import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faClock } from '@fortawesome/pro-solid-svg-icons';
import { isToday } from 'date-fns';
import type { ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';
import {
  FormattedDate,
  FormattedMessage,
  FormattedTime,
  useIntl,
} from 'react-intl';
import styled from 'styled-components';

import {
  PlayerFieldStatus,
  ScoreStatus,
  Sport,
} from '#src/__generated__/globalTypes.ts';
import { Bench } from '#src/atoms/icons/Bench.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { Tooltip } from '#src/atoms/tooltip/Tooltip/index.tsx';
import { BrandXXS, LabelXS } from '#src/atoms/typography/index.tsx';
import { Chip } from '#src/atoms/ui/Chip/index.tsx';
import { Cost } from '#src/components/player/Cost/index.tsx';
import type { Props as ScoringProps } from '#src/components/scoring/Scoring/index.tsx';
import { Scoring } from '#src/components/scoring/Scoring/index.tsx';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { shortStatusMessages, statusMessages } from '#src/lib/players.ts';
import type { ScoreType } from '#src/lib/thresholds.ts';
import { findThresholdColor } from '#src/lib/thresholds.ts';

import type { Score_anyGameInterface } from './__generated__/index.graphql.ts';

const StyledChip = styled(Chip)`
  min-width: var(--quadruple-unit);
  background: var(--c-nd-300-opaque);
  font: var(--t-label-xs);
  font-weight: var(--t-bold);
  font-family: var(--brand-font);
`;

export type Props = {
  score: number;
  scoreStatus: ScoreStatus;
  forceScoreStatus?: ScoreStatus;
  fieldStatus?: PlayerFieldStatus;
  withDate?: boolean;
  hideReviewing?: boolean;
  hidePending?: boolean;
  upcomingGame?: Score_anyGameInterface;
  hasPlayedGames?: boolean;
  scoreType?: ScoreType;
  isCappedMode?: boolean;
  animationDelayMs?: number;
  animated?: boolean;
  withSuffix?: boolean;
  isAffordable?: boolean;
} & Pick<ScoringProps, 'shape' | 'size' | 'inverted' | 'noBorder'>;

const ShapeWrapper = ({
  shape,
  size = 4,
  children,
}: {
  shape: 'PILL' | 'TEXT' | 'HEXAGON';
  size?: Props['size'];
  children: ReactNode;
}) => {
  if (shape === 'TEXT') {
    return (
      <span
        style={{
          color: 'var(--c-nd-600)',
          fontFamily: 'var(--brand-font)',
        }}
      >
        {children}
      </span>
    );
  }

  if (shape === 'PILL') {
    return (
      <StyledChip
        size="medium"
        style={{
          transform: `scale(${size / 4})`,
        }}
      >
        {children}
      </StyledChip>
    );
  }

  return (
    <Cost
      score={
        <LabelXS as="span" color="var(--c-white)" bold>
          {children}
        </LabelXS>
      }
      color="var(--c-nd-150)"
    />
  );
};

const ScoreContent = ({
  score,
  shape = 'PILL',
  scoreStatus,
  forceScoreStatus,
  fieldStatus,
  withDate,
  hideReviewing,
  hidePending = false,
  upcomingGame,
  hasPlayedGames,
  isCappedMode,
  scoreType = 'CARD_SCORE',
  animationDelayMs,
  animated,
  withSuffix,
  size,
  isAffordable = true,
  inverted,
  noBorder,
}: Props) => {
  const { formatMessage } = useIntl();
  const actualScoreStatus = forceScoreStatus || scoreStatus;
  const { sport } = useSportContext();
  const hasNotPlayedAnyGame =
    actualScoreStatus === ScoreStatus.PENDING &&
    ((upcomingGame && !hasPlayedGames) ||
      (upcomingGame === undefined && hasPlayedGames === undefined));

  if (hasNotPlayedAnyGame) {
    if (fieldStatus === PlayerFieldStatus.ON_BENCH) {
      return (
        <ShapeWrapper shape={shape} size={size}>
          <Bench size={2} color="var(--c-nd-600)" />
        </ShapeWrapper>
      );
    }
    if (fieldStatus === PlayerFieldStatus.NOT_ON_GAME_SHEET) {
      return (
        <ShapeWrapper shape={shape} size={size}>
          <FormattedMessage
            {...shortStatusMessages[ScoreStatus.DID_NOT_PLAY]}
          />
        </ShapeWrapper>
      );
    }
    if (upcomingGame) {
      if (withDate) {
        const { date } = upcomingGame;
        return (
          <BrandXXS as="span" bold color="var(--c-white)">
            {isToday(date) ? (
              <FormattedTime value={date} />
            ) : (
              <FormattedDate value={date} weekday="short" day="numeric" />
            )}
          </BrandXXS>
        );
      }
    }
    if (!hidePending) {
      return (
        <ShapeWrapper shape={shape} size={size}>
          <FontAwesomeIcon
            icon={faClock}
            aria-label={formatMessage(statusMessages.PENDING)}
            color="var(--c-nd-600)"
            size={shape === 'HEXAGON' ? 'lg' : undefined}
          />
        </ShapeWrapper>
      );
    }
    return null;
  }

  if (
    scoreStatus === ScoreStatus.REVIEWING &&
    fieldStatus === PlayerFieldStatus.ON_BENCH
  ) {
    return (
      <ShapeWrapper shape={shape} size={size}>
        <Bench size={2} color="var(--c-nd-600)" />
      </ShapeWrapper>
    );
  }

  if (
    [ScoreStatus.DID_NOT_PLAY, ScoreStatus.NO_GAME].includesValue(
      actualScoreStatus
    )
  ) {
    return (
      <ShapeWrapper shape={shape} size={size}>
        <FormattedMessage {...shortStatusMessages[actualScoreStatus]} />
      </ShapeWrapper>
    );
  }

  if (fieldStatus === PlayerFieldStatus.NOT_ON_GAME_SHEET) {
    return (
      <ShapeWrapper shape={shape} size={size}>
        <FormattedMessage {...shortStatusMessages[ScoreStatus.DID_NOT_PLAY]} />
      </ShapeWrapper>
    );
  }

  const roundedScore = Math.floor(score);
  const isReviewing =
    !hideReviewing && actualScoreStatus === ScoreStatus.REVIEWING;

  if (shape === 'HEXAGON' && isCappedMode) {
    return (
      <Scoring
        shape={shape}
        score={roundedScore}
        reviewing={isReviewing}
        delayMs={animationDelayMs}
        animated={animated}
        withSuffix={withSuffix}
        size={size}
        inverted={inverted}
        invalid={isAffordable !== undefined ? !isAffordable : undefined}
      />
    );
  }

  const { color, level } = findThresholdColor(
    roundedScore,
    sport || Sport.FOOTBALL,
    scoreType
  );

  return (
    <Scoring
      shape={shape}
      score={roundedScore}
      color={color}
      glow={level === 'max'}
      reviewing={isReviewing}
      delayMs={animationDelayMs}
      animated={animated}
      withSuffix={withSuffix}
      size={size}
      inverted={inverted}
      noBorder={noBorder}
    />
  );
};

export const Score = ({
  withTooltip,
  tooltipTitle,
  ...rest
}: { withTooltip?: boolean; tooltipTitle?: MessageDescriptor } & Props) => {
  const { scoreStatus, forceScoreStatus } = rest;
  const actualScoreStatus = forceScoreStatus || scoreStatus;
  if (tooltipTitle || withTooltip) {
    return (
      <Tooltip
        title={
          <FormattedMessage
            {...(tooltipTitle || statusMessages[actualScoreStatus])}
          />
        }
      >
        <ScoreContent {...rest} />
      </Tooltip>
    );
  }
  return <ScoreContent {...rest} />;
};

Score.fragments = {
  anyGameInterface: gql`
    fragment Score_anyGameInterface on AnyGameInterface {
      id
      date
    }
  ` as TypedDocumentNode<Score_anyGameInterface>,
};
